import React from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CheckIcon, ClockIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { CheckIcon as CheckIconSolid } from '@heroicons/react/24/solid';
import type { Message } from '../types/sms.types';

interface MessageBubbleProps {
  message: Message;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isFirstInGroup = true,
  isLastInGroup = true
}) => {
  const isOutbound = message.direction === 'outbound';

  const formatTimestamp = (timestamp: string) => {
    try {
      // Timestamp comes as 'YYYY-MM-DD HH:MM:SS' from backend (local time)
      return format(new Date(timestamp.replace(' ', 'T')), 'HH:mm', { locale: fr });
    } catch {
      return '';
    }
  };

  const getStatusIcon = () => {
    if (!isOutbound) return null;

    switch (message.status) {
      case 'sent':
      case 'delivered':
        return (
          <span className="sms-status-icon sms-status-sent" title="Envoyé">
            <CheckIconSolid />
          </span>
        );
      case 'pending':
      case 'fetched':
        return (
          <span className="sms-status-icon sms-status-pending" title="En attente">
            <ClockIcon />
          </span>
        );
      case 'failed':
        return (
          <span className="sms-status-icon sms-status-failed" title="Échec">
            <ExclamationCircleIcon />
          </span>
        );
      default:
        return (
          <span className="sms-status-icon" title="Envoyé">
            <CheckIcon />
          </span>
        );
    }
  };

  // Build class names based on position in group
  const bubbleClasses = [
    'sms-bubble',
    isOutbound ? 'sms-bubble-outbound' : 'sms-bubble-inbound',
    isFirstInGroup ? 'sms-bubble-first' : '',
    isLastInGroup ? 'sms-bubble-last' : '',
    !isFirstInGroup && !isLastInGroup ? 'sms-bubble-middle' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={`sms-bubble-wrapper ${isOutbound ? 'sms-outbound' : 'sms-inbound'}`}>
      <div className={bubbleClasses}>
        <p className="sms-bubble-text">{message.message}</p>
        <div className="sms-bubble-footer">
          <span className="sms-bubble-time">{formatTimestamp(message.timestamp)}</span>
          {getStatusIcon()}
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
